import { themeQuartz } from 'ag-grid-community';
import 'ag-grid-enterprise';
import type { GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiFolderInfoLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import LoadingAnimation from '../../../../components/loadingAnimation';
import { buildFilterQueryParams, formatDateWithTimezoneOffset } from '../../../../helpers/utils';
import type { RootState } from '../../../../state/store';
import { ColumnTypes } from '../../helpers/constants';
import useReportDataContext from '../../hooks/useReportDataContext';
import type { Column, Row } from '../../types/report';
import { buildSubReportPayload, serializeSubReportQuery } from '../../../subR/helpers/querySchema';
import CustomHeaderComponent from '../customHeader';
import SelectSubReport from '../selectSubReport';

interface TableRow {
  [key: string]: string | number | boolean;
}

const ReportTableSection = () => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const companyTimezoneOffset = useSelector(
    (state: RootState) => state?.company?.company_details?.time_zone_offset
  );
  const { dates, timeRange } = useSelector((state: RootState) => state.filters);
  const { reportData, reportConfigs, currencyCode, pageLimit } = useReportDataContext();
  const { columns: columnsData, rows: rowsData } = reportData ?? {
    columns: [] as Column[],
    rows: [] as Row[]
  };
  const [subRData, setSubRData] = useState<any>(null);
  const navigate = useNavigate();
  const routeMatch = useParams();
  const [searchParams] = useSearchParams();
  const gridRef = useRef<any>(null);
  const subRTypeRef = useRef<string | null>(null);
  const initialAutoSizeCompleteRef = useRef<boolean>(false);
  const columnStateLoadedRef = useRef<boolean>(false);

  // Generate unique localStorage key based on dashboard and widget
  const getStorageKey = () => {
    const dashboard = routeMatch.dashboard ?? '';
    const widget = routeMatch.widget ?? '';
    return `rms_column_state_${dashboard}_${widget}`;
  };

  // Save column state to localStorage
  const saveColumnState = () => {
    if (gridRef.current) {
      try {
        const columnState = gridRef.current.getColumnState();
        const storageKey = getStorageKey();
        localStorage.setItem(storageKey, JSON.stringify(columnState));
      } catch (error) {
        console.warn('Failed to save column state:', error);
      }
    }
  };

  // Load column state from localStorage
  const loadColumnState = () => {
    if (gridRef.current && !columnStateLoadedRef.current) {
      try {
        const storageKey = getStorageKey();
        const savedState = localStorage.getItem(storageKey);
        if (savedState) {
          const columnState = JSON.parse(savedState);
          gridRef.current.applyColumnState({
            state: columnState,
            applyOrder: true
          });
          columnStateLoadedRef.current = true;
          return true;
        }
      } catch (error) {
        console.warn('Failed to load column state:', error);
      }
    }
    return false;
  };

  useEffect(() => {
    const primaryColumn = columnsData.find((column) => column.primary_key);
    if (primaryColumn) {
      switch (primaryColumn.accessor) {
        case 'stock_issue_no':
          subRTypeRef.current = 'InventoryStockIssues';
          break;
        case 'order_ref_no':
          subRTypeRef.current = 'InventoryPurchaseValue';
          break;
        case 'stock_receipt_no':
          subRTypeRef.current = 'InventoryStockReceipts';
          break;
        case 'ref_no':
          subRTypeRef.current = 'InventoryStockTakes';
          break;
        default:
          subRTypeRef.current = 'OrderDetails';
      }
    }
  }, [columnsData]);

  // Serial numbers now continue across pages instead of restarting from 1 on each page
  const rows = useMemo<TableRow[]>(() => {
    return rowsData.map((row, index) => {
      // Use pageLimit.startIndex + index to ensure continuous numbering
      // e.g., Page 1: 1-25, Page 2: 26-50, Page 3: 51-75, etc.
      return { ...row, sl_no: pageLimit.startIndex + index };
    });
  }, [rowsData, pageLimit.startIndex]);

  // Column definitions for ag-Grid
  const columnDefs = useMemo(() => {
    // Add serial number column pinned to left
    const serialNumberColumn = {
      headerName: '#',
      headerClass: 'centered-header',
      valueGetter: (params: any) => {
        // Use pagination-aware serial number
        const rowIndex = params.node?.rowIndex || 0;
        return (pageLimit.startIndex || 1) + rowIndex;
      },
      width: 60,
      minWidth: 60,
      flex: 0, // Prevent this column from flexing
      suppressSizeToFit: true, // Prevent sizeColumnsToFit from affecting this column
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      lockPinned: true, // Prevent unpinning
      lockVisible: true, // Prevent hiding
      suppressMovable: true, // Prevent moving
      suppressColumnsToolPanel: true, // Hide from columns tool panel
      cellStyle: (params: any) => ({
        textAlign: 'center',
        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
        borderRight: theme === 'dark' ? '1px solid #404040' : '1px solid #dee2e6',
        fontWeight: '500',
        color: theme === 'dark' ? '#e5e5e5' : '#495057'
      })
    };

    const dynamicColumns = columnsData.map((column) => {
      const colDef: any = {
        headerName: column.header,
        field: column.accessor,
        sortable: false,
        filter: false, // Disable built-in filters
        resizable: true,
        minWidth: 180,
        flex: 1, // Allow columns to flex and fill available space
        wrapHeaderText: false,
        autoHeaderHeight: false,
        // Use custom header component for all columns (decides internally whether to show filters)
        headerComponent: CustomHeaderComponent,
        headerComponentParams: {
          displayName: column.header
        },
        // Pass API to header component via context
        headerComponentParamsGetter: (params: any) => ({
          displayName: column.header,
          column: params.column,
          api: params.api
        }),
        // Add cell styling and click handlers
        cellStyle: (params: any) => getCellStyling(params, column),
        onCellClicked: column.primary_key
          ? (params: any) => handlePrimaryCellClick(params)
          : undefined
      };
      if (column.pinned) {
        colDef.pinned = column.pinned;
      }
      if (column.columns && column.columns.length > 0) {
        colDef.children = column.columns.map((child) => {
          const childColDef: any = {
            field: child.accessor,
            headerName: child.header,
            filter: false,
            sortable: false,
            minWidth: 150,
            resizable: true,
            wrapHeaderText: false,
            autoHeaderHeight: false,
            suppressMovable: child.suppressMovable ?? false,
            // Use custom header component for all nested columns
            headerComponent: CustomHeaderComponent,
            headerComponentParams: {
              displayName: child.header
            },
            headerComponentParamsGetter: (params: any) => ({
              displayName: child.header,
              column: params.column,
              api: params.api
            }),
            // Add cell styling for nested columns
            cellStyle: (params: any) => getCellStyling(params, child)
          };
          if (child.columnGroupShow) {
            childColDef.columnGroupShow = child.columnGroupShow;
          }

          if (child.columns && child.columns.length > 0) {
            childColDef.children = child.columns.map((childChild) => {
              const grandChildColDef: any = {
                field: childChild.accessor,
                headerName: childChild.header,
                filter: false,
                sortable: false,
                minWidth: 120,
                resizable: true,
                wrapHeaderText: false,
                autoHeaderHeight: false,
                suppressMovable: childChild.suppressMovable ?? false,
                // Use custom header component for all nested columns
                headerComponent: CustomHeaderComponent,
                headerComponentParams: {
                  displayName: childChild.header
                },
                headerComponentParamsGetter: (params: any) => ({
                  displayName: childChild.header,
                  column: params.column,
                  api: params.api
                }),
                // Add cell styling for grandchild columns
                cellStyle: (params: any) => getCellStyling(params, childChild)
              };
              if (childChild.columnGroupShow) {
                grandChildColDef.columnGroupShow = childChild.columnGroupShow;
              }

              // Apply formatting based on type for nested columns
              switch (childChild.type) {
                case ColumnTypes.NUMBER:
                  grandChildColDef.type = 'numericColumn';
                  grandChildColDef.cellClass = 'ag-cell-right-align';
                  grandChildColDef.valueFormatter = (params: any) => {
                    return formatNumberValue(childChild.accessor, params.value);
                  };
                  break;
                case ColumnTypes.STRING:
                  grandChildColDef.type = 'string';
                  grandChildColDef.cellClass = 'ag-cell-left-align';
                  break;
                default:
                  grandChildColDef.cellClass = 'ag-cell-left-align';
                  break;
              }
              return grandChildColDef;
            });
          } else {
            // Apply formatting based on type for child columns
            switch (child.type) {
              case ColumnTypes.NUMBER:
                childColDef.type = 'numericColumn';
                childColDef.cellClass = 'ag-cell-right-align';
                childColDef.valueFormatter = (params: any) => {
                  return formatNumberValue(child.accessor, params.value);
                };
                break;
              case ColumnTypes.STRING:
                childColDef.type = 'string';
                childColDef.cellClass = 'ag-cell-left-align';
                break;
              default:
                childColDef.cellClass = 'ag-cell-left-align';
                break;
            }
          }

          return childColDef;
        });
      }

      switch (column.type) {
        case ColumnTypes.NUMBER:
          colDef.type = 'numericColumn';
          colDef.cellClass = 'ag-cell-right-align';
          colDef.valueFormatter = (params: any) => {
            return formatNumberValue(column.accessor, params.value);
          };
          break;
        case ColumnTypes.STRING:
          colDef.type = 'string';
          colDef.cellClass = 'ag-cell-center-align';
          colDef.cellRenderer = (params: any) => renderCellContent(params, column);
          break;
        case ColumnTypes.DATE:
          colDef.type = 'dateColumn';
          colDef.cellClass = 'ag-cell-center-align';
          colDef.valueFormatter = (params: any) => {
            if (params.value) {
              try {
                const timezoneOffset = params.data?.offset || companyTimezoneOffset || '+00:00';
                return formatDateWithTimezoneOffset(
                  params.value.toString(),
                  timezoneOffset,
                  'yyyy-MM-dd hh:mm:ss aa'
                );
              } catch (error) {
                console.warn('Date formatting error:', error);
                return params.value;
              }
            }
            return '';
          };
          break;
        default:
          colDef.cellClass = 'ag-cell-center-align';
          break;
      }

      return colDef;
    });

    return [serialNumberColumn, ...dynamicColumns];
  }, [columnsData, currencyCode, pageLimit.startIndex, theme, companyTimezoneOffset]);

  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 720);

  useEffect(() => {
    const handler = () => setIsMobileViewport(window.innerWidth < 720);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Default column definition
  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: false,
      suppressHeaderMenuButton: true,
      suppressMovable: isMobileViewport,
      // Center align all column headers
      headerClass: 'centered-header'
    }),
    [isMobileViewport]
  );

  const reportTheme = useMemo(() => {
    return theme === 'dark'
      ? themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#1a1a1a',
          borderColor: '#404040',
          borderRadius: 4,
          browserColorScheme: 'dark',
          cellTextColor: '#e5e5e5',
          columnBorder: true,
          fontSize: 14,
          foregroundColor: '#e5e5e5',
          headerBackgroundColor: '#2d2d2d',
          headerFontSize: 14,
          headerTextColor: '#ffffff',
          oddRowBackgroundColor: '#262626',
          wrapperBorderRadius: 8
        })
      : themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#f4f6f6',
          borderRadius: 4,
          browserColorScheme: 'inherit',
          fontSize: 14,
          headerFontSize: 14,
          headerTextColor: '#374151',
          wrapperBorderRadius: 8,
          borderColor: '#d1d5db',
          columnBorder: true,
          headerBackgroundColor: '#d8edea',
          oddRowBackgroundColor: '#ffffff',
          cellTextColor: '#374151',
          foregroundColor: '#374151'
        });
  }, [theme]);

  // Grid options
  const gridOptions = useMemo<GridOptions>(
    () => ({
      animateRows: true,
      enableRangeSelection: false,
      enableCharts: false,
      suppressMenuHide: true,
      pagination: false,
      enableCellTextSelection: false,
      cellSelection: true,
      rowSelection: 'multiple' as const,
      allowContextMenu: false,
      suppressCsvExport: true,
      suppressExcelExport: true,
      // Suppress autosize columns option from context menu
      suppressContextMenu: false,
      // Configure for auto height display
      domLayout: 'autoHeight',
      suppressHorizontalScroll: false,
      suppressVerticalScroll: false,
      // Enable custom header components
      components: {
        customHeaderComponent: CustomHeaderComponent
      },
      // Handle grid ready event to pass API to header components
      onGridReady: (params: any) => {
        // Store API reference for header components
        gridRef.current = params.api;

        // Try to load saved column state
        const stateLoaded = loadColumnState();

        // Initial column sizing when grid is ready - only if no saved state
        if (!initialAutoSizeCompleteRef.current && !stateLoaded) {
          setTimeout(() => {
            params.api.sizeColumnsToFit();
            initialAutoSizeCompleteRef.current = true;
          }, 200);
        }
      },
      // Backup auto-size in case onGridReady didn't work
      onFirstDataRendered: (params: any) => {
        // Only auto-size if no saved state was loaded
        if (!initialAutoSizeCompleteRef.current && !columnStateLoadedRef.current) {
          setTimeout(() => {
            params.api.sizeColumnsToFit();
            params.api.refreshHeader();
            initialAutoSizeCompleteRef.current = true;
          }, 200);
        }
      },
      // Handle when grid size changes
      onGridSizeChanged: (params: any) => {
        // Only auto-size if no saved state exists
        if (!columnStateLoadedRef.current) {
          setTimeout(() => {
            params.api.sizeColumnsToFit();
          }, 200);
        }
      },
      // Save column state when columns are resized
      onColumnResized: (params: any) => {
        // Only save if resize was done by user (not programmatically)
        if (params.finished) {
          saveColumnState();
        }
      }
    }),
    []
  );

  const hasPrimarySubRColumn = useMemo(
    () => columnsData.some((column) => column.primary_key),
    [columnsData]
  );
  const trimTextByWord = (
    text: string,
    maxLength: number
  ): { trimmed: string; needsTrimming: boolean } => {
    if (text.length <= maxLength) {
      return { trimmed: text, needsTrimming: false };
    }

    const words = text.split(' ');
    let trimmed = '';

    for (const word of words) {
      if ((trimmed + ' ' + word).length > maxLength) {
        break;
      }
      trimmed = trimmed ? trimmed + ' ' + word : word;
    }

    return {
      trimmed: trimmed || text.substring(0, maxLength),
      needsTrimming: true
    };
  };

  const renderCellContent = (params: any, column: Column) => {
    const value = params.value;
    const isPrimaryColumn = column.primary_key;

    // Only trim when a primary column exists for sub-R navigation
    if (!hasPrimarySubRColumn) return value;

    // Only apply trimming to string values that are not primary columns
    if (typeof value === 'string' && !isPrimaryColumn) {
      const { trimmed, needsTrimming } = trimTextByWord(value, 50);

      if (needsTrimming) {
        return (
          <span>
            {trimmed}
            <SeeMoreLink onClick={() => handlePrimaryCellClick(params)}>...see more</SeeMoreLink>
          </span>
        );
      }
    }

    return value;
  };

  // Helper to check if a cell value should be wrapped in parentheses based on cell_styles
  const shouldFormatWithParentheses = (accessor: string, rawValue: any) => {
    const cellStyles = reportData?.cell_styles || [];
    return cellStyles.some((style) => style.accessor === accessor && style.value == rawValue);
  };

  // Helper to format a number value, wrapping in parentheses if needed
  const formatNumberValue = (accessor: string, rawValue: any) => {
    if (rawValue === null || rawValue === undefined) return '';

    const value = parseFloat(rawValue);
    if (isNaN(value)) return '';

    if (shouldFormatWithParentheses(accessor, rawValue)) {
      const absValue = Math.abs(value);
      return `(${absValue.toLocaleString()})`;
    }

    return value.toLocaleString();
  };

  const getCellStyling = (params: any, column: Column) => {
    const style: React.CSSProperties = {};
    const value = params.value;
    const cellStyles = reportData?.cell_styles || [];

    const formatCell = cellStyles.find((d) => d.accessor === column.accessor && d.value == value);

    if (formatCell) {
      if (formatCell.color) {
        style.color = formatCell.color;
      }
      if (formatCell.background) {
        style.background = formatCell.background;
      }
      if (formatCell.font_weight) {
        style.fontWeight = formatCell.font_weight;
      }
    }

    // Add primary column styling
    if (column.primary_key) {
      style.color = '#0094ff';
      style.fontWeight = 700;
      style.cursor = 'pointer';
    }

    return style;
  };

  const handlePrimaryCellClick = (params: any) => {
    const currentDashboard = routeMatch.dashboard ?? '';
    const currentWidget = routeMatch.widget;

    // Construct the full widget identifier (dashboard.widget)
    const widgetIdentifier = `${currentDashboard}.${currentWidget}`;

    if (['inventory', 'operations'].includes(currentDashboard)) {
      let primaryColumn;

      // Determine primary column based on widget identifier
      switch (widgetIdentifier) {
        case 'operations.top-sales-performers':
          // For top-sales-performers-report, the primary column would be the staff_id column
          primaryColumn = { accessor: 'staff_id' };
          subRTypeRef.current = 'StaffDetails';
          break;
        default:
          // In inventory dashboard, the primary column would be the id column
          if (currentDashboard === 'inventory') {
            primaryColumn = columnsData.find((col) => col.primary_key);
          } else {
            // for other Operations order type r-level reports
            setSubRData(params.data);
          }
          break;
      }
      if (primaryColumn) {
        const accessorKey = primaryColumn.accessor;
        const rowKey = params.data[accessorKey]; // Get primary key value from row data
        if (accessorKey && rowKey !== null && rowKey !== undefined && subRTypeRef.current) {
          const reportPath = `/${currentDashboard}/${currentWidget}/report/${subRTypeRef.current}`;
          const fromParam = searchParams.get('from');
          const toParam = searchParams.get('to');
          const dateParams =
            fromParam && toParam
              ? { from: fromParam, to: toParam }
              : buildFilterQueryParams({
                  dateRange: dates as [string, string],
                  timeRange: timeRange as [[number, number], [number, number]]
                });

          const consignmentId =
            subRTypeRef.current === 'InventoryPurchaseValue' &&
            params.data?.consignment_id != null &&
            params.data.consignment_id !== ''
              ? String(params.data.consignment_id)
              : undefined;

          const payload = buildSubReportPayload({
            dashboard: currentDashboard,
            widget: currentWidget ?? '',
            reportType: subRTypeRef.current,
            primaryKey: accessorKey,
            primaryValue: String(rowKey),
            from: dateParams.from,
            to: dateParams.to,
            consignmentId
          });

          const queryString = serializeSubReportQuery(payload);
          navigate(queryString ? `${reportPath}${queryString}` : reportPath);
        }
      }
    } else {
      setSubRData(params.data);
    }
  };

  if (reportConfigs.error) {
    return (
      <StyledTableSection>
        <div className="error-message">Error loading report data. Please try again later.</div>
      </StyledTableSection>
    );
  }

  // Add fallback UI for empty data
  if (!rowsData || rowsData.length === 0) {
    return (
      <StyledTableSection>
        <div className="no-data-container">
          {reportConfigs.isLoading && (
            <div className="loading-overlay">
              <LoadingAnimation />
            </div>
          )}
          <RiFolderInfoLine className="no-data-icon" />
          <div className="no-data-message">
            <p>No Data Available. Choose another date range.</p>
          </div>
        </div>
      </StyledTableSection>
    );
  }

  return (
    <StyledTableSection>
      <div className="table-container">
        {reportConfigs.isLoading && (
          <div className="loading-overlay">
            <LoadingAnimation />
          </div>
        )}
        <GridContainer>
          <AgGridReact
            ref={gridRef}
            theme={reportTheme}
            columnDefs={columnDefs}
            rowData={rows}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
          />
        </GridContainer>
      </div>
      {subRTypeRef.current && subRData ? (
        <SelectSubReport
          type={subRTypeRef.current}
          rowData={{ ...subRData, currency: currencyCode }}
          onClose={() => setSubRData(null)}
        />
      ) : null}
    </StyledTableSection>
  );
};

export default ReportTableSection;

const StyledTableSection = styled.div`
  margin: 16px;
  position: relative;
  height: auto;

  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border_color};
    min-height: 400px;
    text-align: center;

    .no-data-icon {
      font-size: 64px;
      margin-bottom: 24px;
      opacity: 0.6;
      color: ${({ theme }) => theme.text};
    }

    .no-data-message {
      p {
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        font-size: 16px;
        line-height: 1.5;
        margin: 0;
        max-width: 400px;
      }
    }
  }

  .table-container {
    background: ${({ theme }) => theme.widget.background};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid ${({ theme }) => theme.border_color};
    overflow: visible;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #91a1ac;
    font-weight: 500;
    backdrop-filter: blur(10px);
    z-index: 2;
    border-radius: 8px;
  }

  .error-message {
    color: #dc3545;
    background: #fff5f5;
    padding: 16px;
    border-radius: 6px;
    margin: 16px 0;
    border: 1px solid #dc3545;
  }
`;

const GridContainer = styled.div`
  height: auto;
  width: 100%;
  position: relative;
  overflow: visible;

  /* Set minimum height for the ag-grid table */
  .ag-root-wrapper {
    min-height: 600px;
  }

  /* Center align all column headers - AG-Grid specific selectors */
  .ag-header-cell-label {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  .ag-header-cell-text {
    text-align: center !important;
    width: 100% !important;
    display: block !important;
  }

  .ag-header-cell-comp-wrapper {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  /* Target the header cells directly */
  .ag-header-cell {
    .ag-header-cell-label {
      justify-content: center !important;
    }
  }

  /* Override any custom header component styles */
  .ag-header-cell .ag-react-container {
    width: 100% !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .ag-cell-right-align {
    justify-content: flex-end;
    text-align: right;
  }

  .ag-cell-left-align {
    justify-content: flex-start;
    text-align: left;
  }

  .ag-cell-center-align {
    justify-content: center;
    text-align: center;
  }

  .ag-header-cell {
    overflow: visible !important;
  }

  .ag-center-cols-container,
  .ag-center-cols-viewport {
    min-height: 30px !important;
    height: auto !important;
  }
`;

const SeeMoreLink = styled.span`
  color: #0094ff;
  cursor: pointer;
  font-weight: 500;
  margin-left: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #0076cc;
  }

  &:active {
    color: #005aa3;
  }
`;
